import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import HikingTrailsService from '../services/HikingTrails';

var STATUS_COLORS = {
  open: '#198754', closed: '#dc3545', partial_closure: '#ffc107',
  seasonal_closure: '#fd7e14', unknown: '#6c757d',
};
var STATUS_LABELS = {
  open: 'Open', closed: 'Closed', partial_closure: 'Partial',
  seasonal_closure: 'Seasonal', unknown: 'Unknown',
};
var DIFFICULTY_COLORS = {
  easy: '#198754', moderate: '#0d6efd', hard: '#fd7e14', expert: '#dc3545',
};
var USE_ICONS = {
  hiking: '\u{1F6B6}', biking: '\u{1F6B2}', horses: '\u{1F40E}', ohv: '\u{1F3CE}',
  snowshoe: '\u{1F97E}', xc_ski: '\u{26F7}', snowmobile: '\u{1F3CD}',
};
var DOGS_DISPLAY = {
  yes: '\u2705', on_leash: '\u{1F6AF}', no: '\u274C', unknown: '\u2014',
};
var TRAIL_TYPE_COLORS = {
  'Hiking': '#198754', 'Multi-Use': '#0d6efd', '4x4/OHV': '#fd7e14',
  'Out & Back': '#6c757d', 'Loop': '#6c757d', 'Point-to-Point': '#6c757d',
};
var PREDICTION_COLORS = {
  likely_open: '#198754', likely_closed: '#dc3545', uncertain: '#ffc107',
  closed: '#dc3545', unknown: '#6c757d',
};

function formatTimestamp(ts) {
  if (!ts) return 'Never';
  var d = new Date(ts);
  var now = new Date();
  var diffMs = now - d;
  var diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return diffMins + 'm ago';
  var diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return diffHrs + 'h ago';
  var diffDays = Math.floor(diffHrs / 24);
  return diffDays + 'd ago';
}

function StatusBadge({ status }) {
  var color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  var label = STATUS_LABELS[status] || status || 'Unknown';
  var textColor = status === 'partial_closure' ? '#000' : '#fff';
  return (
    <span style={{
      backgroundColor: color, color: textColor,
      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>{label}</span>
  );
}

function DifficultyBadge({ difficulty }) {
  if (!difficulty) return <span style={{ color: '#999' }}>{'\u2014'}</span>;
  var color = DIFFICULTY_COLORS[difficulty] || '#6c757d';
  return (
    <span style={{
      backgroundColor: color, color: '#fff',
      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>{difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</span>
  );
}

function TrailTypeBadge({ label }) {
  if (!label) return <span style={{ color: '#999' }}>{'\u2014'}</span>;
  var color = TRAIL_TYPE_COLORS[label] || '#6c757d';
  return (
    <span style={{
      backgroundColor: color, color: '#fff',
      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 'bold',
    }}>{label}</span>
  );
}

function SeasonalInfo({ closures }) {
  if (!closures || closures.length === 0) return <span style={{ color: '#999', fontSize: '0.75em' }}>Year-round</span>;
  return (
    <div style={{ fontSize: '0.72em', lineHeight: '1.3' }}>
      {closures.slice(0, 2).map(function(c, i) {
        return <div key={i} style={{ color: '#dc3545' }}>{c.detail || c.reason || 'Seasonal closure'}</div>;
      })}
    </div>
  );
}

function QualityBar({ score }) {
  var color = score >= 70 ? '#198754' : score >= 40 ? '#ffc107' : '#dc3545';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <div style={{ width: '50px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: score + '%', height: '100%', backgroundColor: color, borderRadius: '4px' }} />
      </div>
      <span style={{ fontSize: '0.7em', color: '#666' }}>{score}</span>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: color || '#f8f9fa', borderRadius: '8px',
      padding: '12px 16px', textAlign: 'center', minWidth: '110px',
    }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold' }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>{label}</div>
    </div>
  );
}

var CELL_STYLE = {
  wordWrap: 'break-word', overflowWrap: 'break-word',
  whiteSpace: 'normal', verticalAlign: 'middle', fontSize: '0.85em',
};

var HikingTrails = function() {
  var _trails = useState([]);
  var trails = _trails[0], setTrails = _trails[1];
  var _stats = useState({});
  var stats = _stats[0], setStats = _stats[1];
  var _filters = useState({ search: '', region: '', county: '', managing_agency: '', status: '', difficulty: '', dogs: '', closures_only: '', needs_check: '' });
  var filters = _filters[0], setFilters = _filters[1];
  var _filterOptions = useState({ regions: [], counties: [], agencies: [] });
  var filterOptions = _filterOptions[0], setFilterOptions = _filterOptions[1];
  var _sortField = useState('name');
  var sortField = _sortField[0], setSortField = _sortField[1];
  var _sortDir = useState('asc');
  var sortDir = _sortDir[0], setSortDir = _sortDir[1];
  var _loading = useState(true);
  var loading = _loading[0], setLoading = _loading[1];
  var _syncing = useState(false);
  var syncing = _syncing[0], setSyncing = _syncing[1];
  var _showLog = useState(false);
  var showLog = _showLog[0], setShowLog = _showLog[1];
  var _syncLogs = useState([]);
  var syncLogs = _syncLogs[0], setSyncLogs = _syncLogs[1];
  var _selectedTrail = useState(null);
  var selectedTrail = _selectedTrail[0], setSelectedTrail = _selectedTrail[1];
  var _detailLoading = useState(false);
  var detailLoading = _detailLoading[0], setDetailLoading = _detailLoading[1];
  var _prediction = useState(null);
  var prediction = _prediction[0], setPrediction = _prediction[1];
  var _predictDate = useState('');
  var predictDate = _predictDate[0], setPredictDate = _predictDate[1];
  var _closureHistory = useState([]);
  var closureHistory = _closureHistory[0], setClosureHistory = _closureHistory[1];

  function loadTrails() {
    setLoading(true);
    var f = Object.assign({}, filters, { sort: sortField, sort_dir: sortDir });
    HikingTrailsService.getTrails(f).then(function(resp) {
      setTrails(resp.trails || []);
      setStats({
        total: resp.total_count, filtered: resp.filtered_count,
        open: resp.open_count, closed: resp.closed_count,
        partial: resp.partial_count, unknown: resp.unknown_count,
        stale: resp.stale_count,
        last_cotrex: resp.last_cotrex_sync, last_usfs: resp.last_usfs_sync,
        last_alerts: resp.last_alerts_sync, last_dw: resp.last_dw_sync,
      });
      setFilterOptions({ regions: resp.regions || [], counties: resp.counties || [], agencies: resp.agencies || [] });
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }

  function loadSyncLog() {
    HikingTrailsService.getSyncLog().then(function(resp) {
      setSyncLogs(resp.logs || []);
    });
  }

  function loadTrailDetail(id) {
    setDetailLoading(true);
    setPrediction(null);
    setClosureHistory([]);
    setPredictDate('');
    HikingTrailsService.getTrail(id).then(function(resp) {
      setSelectedTrail(resp.trail);
      setDetailLoading(false);
      // Load closure history
      HikingTrailsService.getClosureHistory(id).then(function(r) {
        setClosureHistory(r.closures || []);
      });
    }).catch(function() { setDetailLoading(false); });
  }

  function handlePredict(id, date) {
    HikingTrailsService.predict(id, date).then(function(resp) {
      setPrediction(resp.prediction);
    });
  }

  function handleSync(type) {
    setSyncing(true);
    var fn = type === 'cotrex' ? HikingTrailsService.syncCotrex
           : type === 'alerts' ? HikingTrailsService.syncAlerts
           : type === 'dw' ? HikingTrailsService.syncDenverWater
           : HikingTrailsService.syncAll;
    fn().then(function(resp) {
      setSyncing(false);
      alert(resp.message || 'Sync started');
    }).catch(function() { setSyncing(false); });
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  useEffect(function() { loadTrails(); }, [filters, sortField, sortDir]);

  // === DETAIL VIEW ===
  if (selectedTrail) {
    var t = selectedTrail;
    return (
      <div className="m-4">
        <button className="btn btn-sm btn-outline-secondary mb-3" onClick={function() { setSelectedTrail(null); loadTrails(); }}>
          &larr; Back to Trails
        </button>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h3 className="mb-1">{t.name}</h3>
            <span className="text-muted">{t.wb_trail_id} &middot; {t.source_system} &middot; {t.managing_agency || 'Unknown agency'}</span>
          </div>
          <StatusBadge status={t.current_status} />
        </div>

        {t.status_detail && t.current_status !== 'open' && (
          <div style={{ backgroundColor: '#fff3cd', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.9em' }}>
            <strong>Closure Detail:</strong> {t.status_detail}
            {t.closure_authority && <span> &mdash; {t.closure_authority}</span>}
            {t.closure_start_date && <span> ({t.closure_start_date}{t.closure_end_date ? ' to ' + t.closure_end_date : ' - ongoing'})</span>}
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-6">
            <h5>Location</h5>
            <table className="table table-sm">
              <tbody>
                <tr><td style={{width:'40%'}}><strong>Region</strong></td><td>{t.region || '\u2014'}</td></tr>
                <tr><td><strong>County</strong></td><td>{t.county || '\u2014'}</td></tr>
                <tr><td><strong>Nearest City</strong></td><td>{t.nearest_city || '\u2014'}</td></tr>
                <tr><td><strong>Coordinates</strong></td><td>{t.latitude && t.longitude ? t.latitude.toFixed(4) + ', ' + t.longitude.toFixed(4) : '\u2014'}</td></tr>
                <tr><td><strong>Elevation</strong></td><td>{t.elevation_start_ft ? t.elevation_start_ft.toLocaleString() + ' \u2013 ' + (t.elevation_end_ft || '?').toLocaleString() + ' ft' : '\u2014'}</td></tr>
                <tr><td><strong>Gain</strong></td><td>{t.elevation_gain_ft ? t.elevation_gain_ft.toLocaleString() + ' ft' : '\u2014'}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="col-md-6">
            <h5>Trail Info</h5>
            <table className="table table-sm">
              <tbody>
                <tr><td style={{width:'40%'}}><strong>Length</strong></td><td>{t.length_miles ? t.length_miles + ' mi' : '\u2014'}</td></tr>
                <tr><td><strong>Type</strong></td><td>{t.trail_type || '\u2014'}</td></tr>
                <tr><td><strong>Difficulty</strong></td><td><DifficultyBadge difficulty={t.difficulty} /></td></tr>
                <tr><td><strong>Surface</strong></td><td>{t.surface_type || '\u2014'}</td></tr>
                <tr><td><strong>Uses</strong></td><td>{(t.allowed_uses || []).map(function(u) { return USE_ICONS[u] || u; }).join(' ')}</td></tr>
                <tr><td><strong>Dogs</strong></td><td>{DOGS_DISPLAY[t.dogs_allowed] || t.dogs_allowed || '\u2014'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6">
            <h5>Land Management</h5>
            <table className="table table-sm">
              <tbody>
                <tr><td style={{width:'40%'}}><strong>Agency</strong></td><td>{t.managing_agency || '\u2014'}</td></tr>
                <tr><td><strong>Ownership</strong></td><td>{t.land_ownership || '\u2014'}</td></tr>
                <tr><td><strong>National Forest</strong></td><td>{t.national_forest || '\u2014'}</td></tr>
                <tr><td><strong>Wilderness</strong></td><td>{t.wilderness_area || '\u2014'}</td></tr>
                <tr><td><strong>Ranger District</strong></td><td>{t.ranger_district || '\u2014'}</td></tr>
                <tr><td><strong>Permit Required</strong></td><td>{t.requires_permit ? 'Yes' : 'No'}{t.permit_details ? ' \u2014 ' + t.permit_details : ''}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="col-md-6">
            <h5>Access</h5>
            <table className="table table-sm">
              <tbody>
                <tr><td style={{width:'40%'}}><strong>Trailhead</strong></td><td>{t.trailhead_name || '\u2014'}</td></tr>
                <tr><td><strong>Parking</strong></td><td>{t.trailhead_parking || '\u2014'}</td></tr>
                <tr><td><strong>Access Road</strong></td><td>{t.access_road || '\u2014'} {t.access_road_type ? '(' + t.access_road_type + ')' : ''}</td></tr>
                <tr><td><strong>Restrooms</strong></td><td>{t.restrooms_available === true ? 'Yes' : t.restrooms_available === false ? 'No' : '\u2014'}</td></tr>
                <tr><td><strong>Water</strong></td><td>{t.water_available === true ? 'Yes' : t.water_available === false ? 'No' : '\u2014'}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {t.description && (
          <div className="mb-4">
            <h5>Description</h5>
            <p style={{ fontSize: '0.9em', color: '#444' }}>{t.description}</p>
          </div>
        )}

        {t.seasonal_closures && t.seasonal_closures.length > 0 && (
          <div className="mb-4">
            <h5>Known Seasonal Closures</h5>
            <table className="table table-sm">
              <thead><tr><th>Period</th><th>Reason</th><th>Authority</th><th>Detail</th></tr></thead>
              <tbody>
                {t.seasonal_closures.map(function(sc, i) {
                  return <tr key={i}><td>{sc.typical_start} - {sc.typical_end}</td><td>{sc.reason}</td><td>{sc.authority || '\u2014'}</td><td>{sc.detail || '\u2014'}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Predictive Widget */}
        <div className="mb-4" style={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
          <h5>Predict Trail Status</h5>
          <div className="d-flex align-items-center gap-2 mb-2">
            <input type="date" className="form-control form-control-sm" style={{ width: '180px' }}
              value={predictDate}
              onChange={function(e) { setPredictDate(e.target.value); }}
            />
            <button className="btn btn-sm btn-primary"
              disabled={!predictDate}
              onClick={function() { handlePredict(t.id, predictDate); }}>
              Predict
            </button>
          </div>
          {prediction && (
            <div style={{ marginTop: '10px' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{
                  backgroundColor: PREDICTION_COLORS[prediction.predicted_status] || '#6c757d',
                  color: '#fff', padding: '4px 12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.85em',
                }}>{prediction.predicted_status.replace(/_/g, ' ').toUpperCase()}</span>
                <span style={{ fontSize: '0.85em', color: '#666' }}>Confidence: {Math.round(prediction.confidence * 100)}%</span>
              </div>
              {prediction.factors && prediction.factors.map(function(f, i) {
                var impactColor = f.impact === 'positive' ? '#198754' : f.impact === 'negative' ? '#dc3545' : '#6c757d';
                return (
                  <div key={i} style={{ fontSize: '0.8em', padding: '4px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ color: impactColor, fontWeight: 'bold' }}>{f.impact === 'positive' ? '+' : f.impact === 'negative' ? '-' : '='}</span>
                    {' '}<strong>{f.factor}:</strong> {f.detail}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Closure History */}
        {closureHistory.length > 0 && (
          <div className="mb-4">
            <h5>Closure History</h5>
            <table className="table table-sm">
              <thead><tr><th>Start</th><th>End</th><th>Reason</th><th>Authority</th><th>Notes</th></tr></thead>
              <tbody>
                {closureHistory.map(function(c) {
                  return <tr key={c.id}><td>{c.start_date || '\u2014'}</td><td>{c.end_date || 'Ongoing'}</td><td>{c.reason || '\u2014'}</td><td>{c.authority || '\u2014'}</td><td style={{fontSize:'0.8em',maxWidth:'300px'}}>{c.notes ? c.notes.substring(0, 200) : '\u2014'}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mb-4">
          <h5>Metadata</h5>
          <table className="table table-sm">
            <tbody>
              <tr><td style={{width:'30%'}}><strong>Data Quality</strong></td><td><QualityBar score={t.data_quality_score || 0} /></td></tr>
              <tr><td><strong>Source IDs</strong></td><td style={{fontSize:'0.8em'}}>{t.source_ids ? JSON.stringify(t.source_ids) : '\u2014'}</td></tr>
              <tr><td><strong>Last Synced</strong></td><td>{t.last_synced_at ? new Date(t.last_synced_at).toLocaleString() : 'Never'}</td></tr>
              <tr><td><strong>Status Last Checked</strong></td><td>{t.status_last_checked ? new Date(t.status_last_checked).toLocaleString() : 'Never'}</td></tr>
              <tr><td><strong>Notes</strong></td><td>{t.notes || '\u2014'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // === LIST VIEW ===
  return (
    <div className="m-4">
      <BackButton />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Hiking Trails</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={function() { setShowLog(!showLog); if (!showLog) loadSyncLog(); }}>
            {showLog ? 'Hide Log' : 'Sync Log'}
          </button>
          <button className="btn btn-sm btn-outline-primary" disabled={syncing} onClick={function() { handleSync('alerts'); }}>
            {syncing ? 'Syncing...' : 'Sync Alerts'}
          </button>
          <button className="btn btn-sm btn-outline-info" disabled={syncing} onClick={function() { handleSync('dw'); }}>
            Denver Water
          </button>
          <button className="btn btn-sm btn-primary" disabled={syncing} onClick={function() { handleSync('all'); }}>
            {syncing ? 'Syncing...' : 'Full Sync'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        <SummaryCard label="Total Trails" value={stats.total || 0} />
        <SummaryCard label="Open" value={stats.open || 0} color="#d1e7dd" />
        <SummaryCard label="Closed" value={stats.closed || 0} color="#f8d7da" />
        <SummaryCard label="Partial" value={stats.partial || 0} color="#fff3cd" />
        <SummaryCard label="Unknown" value={stats.unknown || 0} color="#e2e3e5" />
        <SummaryCard label="Needs Check" value={stats.stale || 0} color={stats.stale > 0 ? '#ffeeba' : '#f8f9fa'} />
      </div>

      {/* Sync timestamps */}
      <div style={{ fontSize: '0.75em', color: '#888', marginBottom: '12px' }}>
        COTREX: {formatTimestamp(stats.last_cotrex)} &middot; USFS: {formatTimestamp(stats.last_usfs)} &middot; Alerts: {formatTimestamp(stats.last_alerts)} &middot; Denver Water: {formatTimestamp(stats.last_dw)}
      </div>

      {/* Sync Log */}
      {showLog && (
        <div className="mb-3">
          <h5>Sync Log</h5>
          <table className="table table-sm table-striped" style={{ fontSize: '0.8em' }}>
            <thead><tr><th>Job</th><th>Started</th><th>Status</th><th>Processed</th><th>Created</th><th>Updated</th><th>Matched</th><th>Duration</th><th>Error</th></tr></thead>
            <tbody>
              {syncLogs.map(function(l, i) {
                return (
                  <tr key={i}>
                    <td>{l.job_name}</td>
                    <td>{formatTimestamp(l.started_at)}</td>
                    <td><span style={{ color: l.status === 'success' ? '#198754' : l.status === 'error' ? '#dc3545' : '#ffc107', fontWeight: 'bold' }}>{l.status}</span></td>
                    <td>{l.records_processed}</td>
                    <td>{l.records_created}</td>
                    <td>{l.records_updated}</td>
                    <td>{l.records_matched || 0}</td>
                    <td>{l.duration_ms ? (l.duration_ms / 1000).toFixed(1) + 's' : '\u2014'}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.error_message || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <input type="text" className="form-control form-control-sm" placeholder="Search trails..."
            value={filters.search}
            onChange={function(e) { setFilters(Object.assign({}, filters, { search: e.target.value })); }}
          />
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.region}
            onChange={function(e) { setFilters(Object.assign({}, filters, { region: e.target.value })); }}>
            <option value="">All Regions</option>
            {filterOptions.regions.map(function(r) { return <option key={r} value={r}>{r}</option>; })}
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.status}
            onChange={function(e) { setFilters(Object.assign({}, filters, { status: e.target.value })); }}>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="partial_closure">Partial Closure</option>
            <option value="seasonal_closure">Seasonal Closure</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        <div className="col-md-2">
          <select className="form-select form-select-sm" value={filters.difficulty}
            onChange={function(e) { setFilters(Object.assign({}, filters, { difficulty: e.target.value })); }}>
            <option value="">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="moderate">Moderate</option>
            <option value="hard">Hard</option>
            <option value="expert">Expert</option>
          </select>
        </div>
        <div className="col-md-1">
          <select className="form-select form-select-sm" value={filters.allowed_use || ''}
            onChange={function(e) { setFilters(Object.assign({}, filters, { allowed_use: e.target.value })); }}>
            <option value="">Trail Type</option>
            <option value="hiking">Hiking</option>
            <option value="biking">Biking</option>
            <option value="ohv">4x4/OHV</option>
            <option value="horses">Horses</option>
          </select>
        </div>
        <div className="col-md-2">
          <div className="d-flex gap-1">
            <button className={'btn btn-sm ' + (filters.closures_only === 'true' ? 'btn-warning' : 'btn-outline-secondary')}
              onClick={function() { setFilters(Object.assign({}, filters, { closures_only: filters.closures_only === 'true' ? '' : 'true' })); }}>
              Closures
            </button>
            <button className={'btn btn-sm ' + (filters.needs_check === 'true' ? 'btn-warning' : 'btn-outline-secondary')}
              onClick={function() { setFilters(Object.assign({}, filters, { needs_check: filters.needs_check === 'true' ? '' : 'true' })); }}>
              Stale
            </button>
          </div>
        </div>
      </div>

      <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '8px' }}>
        Showing {trails.length} of {stats.filtered || 0} trails (filtered from {stats.total || 0} total)
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="text-center p-4"><em>Loading trails...</em></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm table-hover table-striped" style={{ fontSize: '0.82em' }}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('name'); }}>
                  Trail Name {sortField === 'name' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('region'); }}>
                  Region {sortField === 'region' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>County</th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('managing_agency'); }}>
                  Agency {sortField === 'managing_agency' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('length_miles'); }}>
                  Length {sortField === 'length_miles' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Elevation</th>
                <th>Type</th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('difficulty'); }}>
                  Difficulty {sortField === 'difficulty' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Uses</th>
                <th>Dogs</th>
                <th style={{ cursor: 'pointer' }} onClick={function() { handleSort('current_status'); }}>
                  Status {sortField === 'current_status' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </th>
                <th>Seasonal Closures</th>
                <th>Info</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {trails.map(function(t) {
                var elevRange = '';
                if (t.elevation_start_ft && t.elevation_end_ft) {
                  elevRange = t.elevation_start_ft.toLocaleString() + ' \u2013 ' + t.elevation_end_ft.toLocaleString() + ' ft';
                } else if (t.elevation_start_ft) {
                  elevRange = t.elevation_start_ft.toLocaleString() + ' ft';
                }
                return (
                  <tr key={t.id}>
                    <td style={Object.assign({}, CELL_STYLE, { fontWeight: 'bold', cursor: 'pointer', color: '#0d6efd', maxWidth: '200px' })}
                      onClick={function() { loadTrailDetail(t.id); }}>
                      {t.name}
                      <div style={{ fontSize: '0.75em', color: '#999', fontWeight: 'normal' }}>{t.wb_trail_id}</div>
                    </td>
                    <td style={CELL_STYLE}>{t.region || '\u2014'}</td>
                    <td style={CELL_STYLE}>{t.county || '\u2014'}</td>
                    <td style={Object.assign({}, CELL_STYLE, { maxWidth: '150px' })}>{t.managing_agency || '\u2014'}</td>
                    <td style={CELL_STYLE}>{t.length_miles ? t.length_miles.toFixed(1) + ' mi' : '\u2014'}</td>
                    <td style={Object.assign({}, CELL_STYLE, { fontSize: '0.75em' })}>{elevRange || '\u2014'}</td>
                    <td style={CELL_STYLE}><TrailTypeBadge label={t.trail_type_label} /></td>
                    <td style={CELL_STYLE}><DifficultyBadge difficulty={t.difficulty} /></td>
                    <td style={CELL_STYLE}>{(t.allowed_uses || []).map(function(u) { return USE_ICONS[u] || ''; }).join('')}</td>
                    <td style={CELL_STYLE}>{DOGS_DISPLAY[t.dogs_allowed] || '\u2014'}</td>
                    <td style={CELL_STYLE}><StatusBadge status={t.current_status} /></td>
                    <td style={Object.assign({}, CELL_STYLE, { maxWidth: '180px' })}><SeasonalInfo closures={t.seasonal_closures} /></td>
                    <td style={CELL_STYLE}>
                      {t.trail_url && <a href={t.trail_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75em' }}>Link</a>}
                    </td>
                    <td style={CELL_STYLE}>
                      <button className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.75em', padding: '1px 6px' }}
                        onClick={function() { loadTrailDetail(t.id); }}>
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HikingTrails;
